import React from 'react';
import { Sun, Moon, CloudRain, Cloudy, CloudLightning, RotateCw, Sparkles, RefreshCw } from 'lucide-react';
import { useStore } from '../store';
import { shared } from '../three/shared';

const label = (v) => (v < 0.23 ? 'NIGHT' : v < 0.32 ? 'DAWN' : v < 0.68 ? 'DAY' : v < 0.78 ? 'DUSK' : 'NIGHT');

export default function EnvironmentDeck() {
  const timeLabel = useStore((s) => s.timeLabel);
  const timeValue = useStore((s) => s.timeValue);
  const autoCycle = useStore((s) => s.autoCycle);
  const weather = useStore((s) => s.weather);
  const setWeather = useStore((s) => s.setWeather);
  const setTimeValue = useStore((s) => s.setTimeValue);
  const toggleAutoCycle = useStore((s) => s.toggleAutoCycle);
  const setEnvHud = useStore((s) => s.setEnvHud);
  const setStormActive = useStore((s) => s.setStormActive);
  const reshuffle = useStore((s) => s.reshuffle);
  const resetEnv = useStore((s) => s.resetEnv);
  const rainIntensity = useStore((s) => s.rainIntensity);
  const setRainIntensity = useStore((s) => s.setRainIntensity);
  const setAuto = (v) => useStore.setState({ autoCycle: v });

  const jumpTo = (v) => {
    setAuto(false);
    shared.env.dayTime = v;
    setTimeValue(v);
    setEnvHud(label(v), v < 0.23 || v >= 0.78);
  };
  const onSlider = (e) => {
    const v = parseFloat(e.target.value);
    setAuto(false);
    shared.env.dayTime = v;
    setTimeValue(v);
    setEnvHud(label(v), v < 0.23 || v >= 0.78);
  };
  const pickWeather = (w) => { setWeather(w); };
  const rainLabel = rainIntensity < 0.02 ? 'CLEAR' : rainIntensity < 0.4 ? 'LIGHT' : rainIntensity < 0.75 ? 'MODERATE' : 'HEAVY';

  const Wbtn = ({ id, w, icon: Icon, title }) => (
    <button data-testid={id} onClick={() => pickWeather(w)} title={title}
      className={`p-2 rounded-lg transition-colors ${weather === w ? 'text-gold bg-white/10' : 'text-white/50 hover:text-gold'}`}>
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div data-testid="env-hud" className="glass rounded-2xl px-4 py-3 w-64 flex flex-col gap-3 pointer-events-auto">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40">Environment Deck</span>
        <span className="font-mono text-[11px] font-bold text-gold tracking-widest" data-testid="time-label">{timeLabel}</span>
      </div>

      {/* Time of day slider */}
      <div className="flex items-center gap-2">
        <button data-testid="skip-night-btn" onClick={() => jumpTo(0.0)} title="Midnight" className="text-white/60 hover:text-gold"><Moon className="w-3.5 h-3.5" /></button>
        <input
          data-testid="time-slider" type="range" min="0" max="1" step="0.01"
          value={timeValue} onChange={onSlider}
          className="flex-1 accent-[#D4AF37] h-1"
        />
        <button data-testid="skip-day-btn" onClick={() => jumpTo(0.5)} title="Noon" className="text-white/60 hover:text-gold"><Sun className="w-3.5 h-3.5" /></button>
      </div>

      <div className="flex items-center justify-between">
        {/* Weather presets (background) */}
        <div className="flex items-center gap-1">
          <Wbtn id="weather-sunny-btn" w="sunny" icon={Sun} title="Sunny" />
          <Wbtn id="weather-mist-btn" w="mist" icon={Cloudy} title="Mist" />
        </div>
        {/* Auto-cycle toggle */}
        <button data-testid="autocycle-toggle" onClick={toggleAutoCycle} title="Auto-Cycle"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest transition-colors ${autoCycle ? 'text-gold bg-gold/10 border border-gold/40' : 'text-white/45 border border-white/15'}`}>
          <RotateCw className={`w-3 h-3 ${autoCycle ? 'animate-spin' : ''}`} style={{ animationDuration: '4s' }} />
          Auto
        </button>
      </div>

      {/* Outrage-Storm rain intensity: Light → Moderate → Heavy */}
      <div className="flex items-center gap-2" title="Purple Rain intensity">
        <CloudLightning className={`w-4 h-4 shrink-0 ${rainIntensity > 0.02 ? 'text-[#a78bfa]' : 'text-white/40'}`} />
        <input
          data-testid="rain-intensity-slider" type="range" min="0" max="1" step="0.01"
          value={rainIntensity} onChange={(e) => setRainIntensity(parseFloat(e.target.value))}
          className="flex-1 accent-[#8b5cf6] h-1"
        />
        <span data-testid="rain-label" className="font-mono text-[9px] font-bold tracking-widest w-16 text-right"
          style={{ color: rainIntensity > 0.02 ? '#a78bfa' : 'rgba(255,255,255,0.4)' }}>{rainLabel}</span>
      </div>

      {/* Organic Randomizer — reshuffle time, weather and every procedural spawn */}
      <div className="flex items-center gap-2 pt-2 border-t border-white/10">
        <button data-testid="randomize-env-btn" onClick={reshuffle} title="Reshuffle the entire biome"
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gold/15 border border-gold/40 text-gold text-[10px] font-bold uppercase tracking-widest hover:bg-gold/25 transition-colors">
          <Sparkles className="w-3.5 h-3.5" /> Randomize
        </button>
        <button data-testid="reset-env-btn" onClick={resetEnv} title="Restore the living auto-cycle"
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-white/60 text-[10px] font-bold uppercase tracking-widest hover:text-gold hover:border-gold/40 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Reset
        </button>
      </div>
    </div>
  );
}
